import { Outlet, useParams, useLocation, useNavigate } from 'react-router-dom';
import { Bot, BarChart3, Eye, History, Shield, AlertTriangle } from 'lucide-react';
import { useSystemAgent } from '@/hooks/systemAgents';
import useWorkflowErrors from '@/hooks/workflows/useWorkflowErrors';
import resolveSystemAgentDetailsBasePath from '../helpers/resolveSystemAgentDetailsBasePath';
import resolveSystemAgentPartitionOrganizationId from '../helpers/resolveSystemAgentPartitionOrganizationId';
import {
    AgentMetaRow,
    AgentNameGroup,
    AgentNameRow,
    DetailContainer,
    DetailTopBar,
    DetailTopBarLeft,
    TabButton,
    TabContent,
    TabList,
} from './styled';

const SystemAgentDetails = () => {
    const { agentId, systemAgentId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const organizationId = resolveSystemAgentPartitionOrganizationId({
        routeOrganizationId: agentId,
    });
    const showAccessTab = Boolean(agentId);

    const { systemAgent, isLoadingSystemAgent } = useSystemAgent({
        agentType: systemAgentId || '',
        enabled: !!systemAgentId,
    });
    const { errors: systemAgentErrors, isLoading: isLoadingSystemAgentErrors } = useWorkflowErrors({
        workflowId: `agent:${systemAgentId}`,
        organizationId,
        limit: 100,
        enabled: !!systemAgentId,
    });

    const isHistoryTab = location.pathname.endsWith('/history');
    const isStatsTab = location.pathname.endsWith('/stats');
    const isAccessTab = showAccessTab && location.pathname.endsWith('/access');
    const isErrorsTab = location.pathname.endsWith('/errors');
    const isOverviewTab = !isHistoryTab && !isStatsTab && !isAccessTab && !isErrorsTab;
    const systemAgentErrorsCountLabel = isLoadingSystemAgentErrors
        ? 'Errors'
        : systemAgentErrors.length >= 100
          ? 'Errors (100+)'
          : `Errors (${systemAgentErrors.length})`;

    if (isLoadingSystemAgent) {
        return (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
                Loading system agent...
            </div>
        );
    }

    if (!systemAgentId || !systemAgent) {
        return (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
                System agent not found.
            </div>
        );
    }

    const basePath = resolveSystemAgentDetailsBasePath({
        agentId,
        systemAgentId,
    });

    const handleNavigateToOverview = () => {
        navigate(basePath);
    };

    const handleNavigateToHistory = () => {
        navigate(`${basePath}/history`);
    };

    const handleNavigateToStats = () => {
        navigate(`${basePath}/stats`);
    };

    const handleNavigateToAccess = () => {
        navigate(`${basePath}/access`);
    };

    const handleNavigateToErrors = () => {
        navigate(`${basePath}/errors`);
    };

    return (
        <DetailContainer>
            <DetailTopBar>
                <DetailTopBarLeft>
                    <div className="h-10 w-10 flex-shrink-0 rounded-lg flex items-center justify-center bg-blue-50 text-blue-600">
                        <Bot size={20} />
                    </div>
                    <AgentNameGroup>
                        <AgentNameRow>{systemAgent.name}</AgentNameRow>
                        <AgentMetaRow>
                            <span className="text-xs text-gray-400">v{systemAgent.version}</span>
                        </AgentMetaRow>
                    </AgentNameGroup>
                </DetailTopBarLeft>
            </DetailTopBar>

            <TabList>
                <TabButton
                    $isActive={isOverviewTab}
                    onClick={handleNavigateToOverview}
                    type="button"
                >
                    <Eye size={14} />
                    Overview
                </TabButton>
                <TabButton $isActive={isHistoryTab} onClick={handleNavigateToHistory} type="button">
                    <History size={14} />
                    History
                </TabButton>
                <TabButton $isActive={isStatsTab} onClick={handleNavigateToStats} type="button">
                    <BarChart3 size={14} />
                    Stats
                </TabButton>
                {showAccessTab ? (
                    <TabButton
                        $isActive={isAccessTab}
                        onClick={handleNavigateToAccess}
                        type="button"
                    >
                        <Shield size={14} />
                        Access
                    </TabButton>
                ) : null}
                <TabButton $isActive={isErrorsTab} onClick={handleNavigateToErrors} type="button">
                    <AlertTriangle size={14} />
                    {systemAgentErrorsCountLabel}
                </TabButton>
            </TabList>

            <TabContent>
                <Outlet />
            </TabContent>
        </DetailContainer>
    );
};

export default SystemAgentDetails;
